<template>
  <PageHeader :showBreadcrumb="true" />
  <form @submit.prevent="onSubmit" aria-labelledby="form-title">
    <!-- Instruction -->
    <h2>Instructions</h2>
    <div>
      <p>&bull; To assist you in addressing your complaint, you must complete all the sections below. You can also upload documentation and any supporting evidence that will help in the review of your complaint. After signing the Consent to Release on the last screen, you will receive an email with your complaint number after you submit it. Please keep this number for your records.</p>
      <p>&bull; MDH will review your complaint, and either open an investigation or notify you if they are unable to conduct an investigation, and provide other options.</p>
      <p>&bull; If MDH is able to open an investigation, they may contact you, your HMO, or your providers for additional information.</p>
      <p>&bull; More information on complaints is available on the Complaint Process (health.mn.us) website (link: <a v-bind:href="'https://www.health.state.mn.us/facilities/insurance/managedcare/complaint/investigate.html'">https://www.health.state.mn.us/facilities/insurance/managedcare/complaint/investigate.html</a>). If you have additional questions, please email health.mcs@state.mn.us.</p>
    </div>

    <hr/>
    <!-- Patient Section -->
    <fieldset>
      <legend>Patient Information</legend>
      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="firstName"
            label="First Name"
            v-model="vPatient.firstName.$model"
            :validation="vPatient.firstName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'First Name' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="middleName"
            label="Middle Name"
            v-model="patient.middleName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Middle Name' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="lastName"
            label="Last Name"
            v-model="vPatient.lastName.$model"
            :validation="vPatient.lastName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Last Name' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="dob"
            label="Date of Birth"
            v-model="vPatient.dob.$model"
            :validation="vPatient.dob"
            :inputComponent="DatePicker"
            :inputAttrs="{
              placeholder: 'MM/DD/YYYY',
              dateFormat: 'mm/dd/yy',
              showIcon: true,
              showButtonBar: true,
              class: 'form-control mb-2',
              maxDate: new Date()
            }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="phoneNumber"
            label="Phone Number"
            v-model="patient.phoneNumber"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Phone Number', mask: '(999) 999-9999' }"
          />
        </MnColumn>
        <MnColumn cols="4">
          <ValidatedInput
            id="email"
            label="Email"
            v-model="vPatient.email.$model"
            :validation="vPatient.email"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Email' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="patientRelationship"
            label="How are you related to the patient?"
            v-model="vPatient.relationshipToPatient.$model"
            :validation="vPatient.relationshipToPatient"
            :inputComponent="Select"
            :inputAttrs="{
              options: patientRelationshipOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Relationship'
            }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset v-if="patient.relationshipToPatient && patient.relationshipToPatient !== CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS.Self">
      <legend>Authorized Representative</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="authRepName"
            label="Your Name"
            v-model="vAuthorizedRep.fullName.$model"
            :validation="vAuthorizedRep.fullName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Your Full Name' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="authRepPhone"
            label="Your Primary Phone Number"
            v-model="vAuthorizedRep.phoneNumber.$model"
            :validation="vAuthorizedRep.phoneNumber"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Phone Number', mask: '(999) 999-9999' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="authRepEmail"
            label="Your Email Address"
            v-model="vAuthorizedRep.email.$model"
            :validation="vAuthorizedRep.email"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Email Address' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset>
      <legend>Patient Address</legend>
      <MnRow>
        <MnColumn cols="8">
          <ValidatedInput
            id="street"
            label="Street"
            v-model="vPatient.street.$model"
            :validation="vPatient.street"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Street' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="city"
            label="City"
            v-model="vPatient.city.$model"
            :validation="vPatient.city"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'City' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="state"
            label="State"
            v-model="vPatient.state.$model"
            :validation="vPatient.state"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'State' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="zipCode"
            label="Zip Code"
            v-model="vPatient.zipCode.$model"
            :validation="vPatient.zipCode"
            :inputComponent="InputMask"
            :inputAttrs="{ placeholder: 'Zip Code', mask: '99999?-9999' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset>
      <legend>Health Plan Information</legend>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="healthInsuranceCompany"
            label="Health Insurance Company"
            v-model="vHealthPlan.healthInsuranceCompany.$model"
            :validation="vHealthPlan.healthInsuranceCompany"
            :inputComponent="Select"
            :inputAttrs="{
              options: healthInsuranceCompanyOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Health Insurance Company'
            }"
          />
        </MnColumn>

        <MnColumn v-if="healthPlan.healthInsuranceCompany && healthPlan.healthInsuranceCompany === CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES.Other" cols="4">
          <ValidatedInput
            id="otherHmoName"
            label="Other Health Insurance Company Name"
            v-model="healthPlan.otherHmoName.$model"
            :validation="healthPlan.otherHmoName"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Health Insurance Company' }"
          />
        </MnColumn>

        <MnColumn cols="4">
          <ValidatedInput
            id="patientInsuranceId"
            label="Patient Insurance ID"
            v-model="vHealthPlan.patientInsuranceId.$model"
            :validation="vHealthPlan.patientInsuranceId"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Insurance ID#' }"
          />
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="typeOfCoverage"
            label="Type of Coverage"
            v-model="healthPlan.typeOfCoverage"
            :inputComponent="Select"
            :inputAttrs="{
              options: typeOfCoverageOptions,
              optionLabel: 'value',
              optionValue: 'key',
              placeholder: 'Select Coverage Type'
            }"
          />
        </MnColumn>

        <MnColumn v-if="healthPlan.typeOfCoverage && healthPlan.typeOfCoverage === CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES.Other" cols="4">
          <ValidatedInput
            id="otherHmoName"
            label="Other Type of Coverage"
            v-model="healthPlan.otherTypeOfCoverage"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Type of Coverage' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset>
      <legend>Summary of Complaint</legend>
      <MnRow>
        <MnColumn cols="12">
          <ValidatedInput
            id="summaryOfComplaint"
            label="Summary of Complaint"
            v-model="vFormData.summaryOfComplaint.$model"
            :validation="vFormData.summaryOfComplaint"
            :inputComponent="TextArea"
            :inputAttrs="{ placeholder: 'Describe the reason for your complaint', maxlength: 3000, rows: 5 }"
          />
          <div class="char-count">
            {{ currentSummaryLength }} / 3000 characters used
          </div>
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset>
      <legend>Supporting Documents</legend>
      <p>If possible, attach copies of any relevant documents such as referrals, denials, prior authorizations, bills, explanation of benefits, screen shots, and written correspondence with your HMO or provider that are relevant to your consumer complaint.  For example, you can upload:</p>
      <ul>
        <li>A statement describing the complaint.</li>
        <li>Statements and/or letters of medical necessity from your healthcare provider(s)</li>
        <li>A copy of the most recent denial letter from your insurer. Any supporting documents (e.g., medical records, peer-reviewed studies, photos, etc.)</li>
      </ul>
      <p>Upload Option (Word, PDF, jpeg, Png, jpg) for the consumer to upload as many as documents needed (Limit to 20 files and 250 MB)</p>

      <Checkbox
        inputId="isNothingToUpload"
        v-model="isNothingToUpload"
        :binary="true"
      />
      <label for="isNothingToUpload" class="mb-0 mx-2">I don't have anything to upload.</label>

      <MnRow v-if="!isNothingToUpload">
        <MnColumn cols="6">
          <MNFileUpload
            module-type="Consumer Complaint Supporting Documents"
            ref="consumerComplaintSupportingDocuments"
            name="consumerComplaintSupportingDocuments"
            accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg,image/jpg"
            :fileLimit="20"/>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Release of Information from Providers</legend>
      <p>MDH may need to contact your providers related to your complaint.  Providers may include clinics, physicians, hospitals, pharmacies, and any other provider that may have protected health information (PHI) relevant to your complaint. For each provider you list, please indicate the dates of service, or time period, that is relevant to your complaint. By signing this form and submitting your complaint, you are giving MDH permission to contact and obtain information from your providers. </p>
      <div v-for="(provider, index) in providers" :key="index" class="mb-4 border p-3 rounded bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-3">Provider {{ index + 1 }}</h5>
          <Button icon="pi pi-trash" severity="danger" @click="removeProvider(index)" v-if="providers.length > 1" />
        </div>

        <MnRow>
          <MnColumn cols="4">
            <ValidatedInput
              :id="`providerName_${index}`"
              label="Name"
              v-model="providers[index].name"
              :validation="vProviders[index].name"
              :inputComponent="InputText"
              :inputAttrs="{ placeholder: 'Provider Name' }"
            />
          </MnColumn>

          <MnColumn cols="4">
            <ValidatedInput
              :id="`providerPhone_${index}`"
              label="Provider Phone Number"
              v-model="providers[index].phoneNumber"
              :validation="vProviders[index].phoneNumber"
              :inputComponent="InputMask"
              :inputAttrs="{ placeholder: 'Provider Phone Number', mask: '(999) 999-9999' }"
            />
          </MnColumn>

          <MnColumn cols="4">
            <ValidatedInput
              :id="`serviceDateFrom_${index}`"
              label="Date of Service From"
              v-model="providers[index].serviceDateFrom"
              :validation="vProviders[index].serviceDateFrom"
              :inputComponent="DatePicker"
              :inputAttrs="{
                placeholder: 'MM/DD/YYYY',
                dateFormat: 'mm/dd/yy',
                showIcon: true,
                showButtonBar: true,
                class: 'form-control mb-2'
              }"
            />
          </MnColumn>

          <MnColumn cols="4">
            <ValidatedInput
              :id="`serviceDateTo_${index}`"
              label="Date of Service To"
              v-model="providers[index].serviceDateTo"
              :validation="vProviders[index].serviceDateTo"
              :inputComponent="DatePicker"
              :inputAttrs="{
                placeholder: 'MM/DD/YYYY',
                dateFormat: 'mm/dd/yy',
                showIcon: true,
                showButtonBar: true,
                class: 'form-control mb-2'
              }"
            />
          </MnColumn>
        </MnRow>
      </div>

      <div class="mt-3">
        <Button
          icon="pi pi-plus"
          label="Add Provider"
          @click="addProvider"
          :disabled="providers.length >= MAX_PROVIDERS"
        />
        <span v-if="providers.length >= MAX_PROVIDERS" class="text-danger ms-2">Maximum 10 providers allowed.</span>
      </div>
    </fieldset>

    <fieldset>
      <legend>Type of Complaint</legend>
      <p>There are different types of complaints that can be filed with MDH. MDH will review the information you provide to determine whether your concern can be handled as a complaint or if it involves a denial that requires a medical opinion.</p>
      <p>If your issue involves a denial that needs a medical opinion, it may be handled as an appeal through an independent review organization (IRO) that uses clinical experts.</p>
      <p>Please indicate below if any of the following apply to your complaint: </p>
      <MnRow>
        <MnColumn cols="12">
          <div v-for="([key, label], index) in Object.entries(CONSUMER_COMPLAINT_TYPES)" :key="index" class="d-flex align-items-center mb-2">
            <Checkbox
              :inputId="`complaintType_${key}`"
              v-model="complaintTypes"
              :value="key"
            />
            <label :for="`complaintType_${key}`" class="mb-0 mx-2">{{ label }}</label>
          </div>
        </MnColumn>
        <MnColumn cols="12">
          <!-- Optional manual explanation -->
          <div class="mt-2" v-if="selectedComplaintTypes.includes('SomethingElse')">
            <label for="manualComplaintExplanation">Please explain:</label>
            <TextArea
              id="manualComplaintExplanation"
              v-model="manualComplaintExplanation"
              rows="3"
              placeholder="Provide additional details (optional)"
              class="manual-explanation-textarea"
            />
          </div>
        </MnColumn>
      </MnRow>
    </fieldset>

    <fieldset>
      <legend>Acknowledgement and sign-off (Complaint)</legend>
      <p>As part of your complaint, MDH may find it helpful to send a copy of your complaint to your HMO. Unless you tell us not to, a copy of your complaint may be sent to your HMO.</p>
      <MnRow>
        <MnColumn cols="12">
          <div class="d-flex align-items-center mb-2">
            <Checkbox
              id="doNotSendCopyToHmo"
              v-model="form.state.data.doNotSendCopyToHmo"
              :binary="true"
            />
            <label for="doNotSendCopyToHmo" class="mb-0 mx-2">Do not send a copy of my complaint to my HMO.
              <span style="color: green;">
                I understand this may negatively impact MDH’s ability to get relevant information from my HMO.
              </span>
            </label>
          </div>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Signature, Attestation and Consent to Release Information</legend>

      <p>I certify that all the information in this form is true to the best of my knowledge. I am insured by the above health plan company.
        To go through an consumer complaint or an appeal, I understand that I need to have gone through my health plan company’s internal complaint process.
        I authorize my health plan company and my medical providers to release my medical records to the Minnesota Department of Health solely for the purpose of reviewing my complaint or appeal.
        This consent will be revoked upon the conclusion of the investigation or appeal.
        I authorize the Minnesota Department of Health to forward any documents connected to my appeal to a state-approved IRO contract vendor.</p>
      <p>I understand it may be necessary for MDH to obtain copies of protected health information for MDH to fully investigate my complaint.
        My signature below is my signed permission allowing MDH to obtain this information.</p>
      <p>If an authorized representative is designated above, that person is authorized to represent me in the consumer complaint or appeal process.</p>
      <p>By typing my name in the signature field, I understand that I am electronically signing this form. I attest and certify that the information provided is true and accurate.
        I understand that my electronic signature has the same legal effect and can be enforced in the same way as a handwritten signature. (MN Statutes, Chapter 325L.07)</p>

      <MnRow>
        <MnColumn cols="4">
          <ValidatedInput
            id="signature"
            label="Signature"
            v-model="form.state.$v.signature.$model"
            :validation="form.state.$v.signature"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Your Full Name' }"
          />
        </MnColumn>

        <MnColumn cols="8">
          <ReadOnlyText label="Date" :value="todayDate">{{ todayDate }}</ReadOnlyText>
        </MnColumn>

        <MnColumn cols="4" v-if="form.state.data.patient.relationshipToPatient && form.state.data.patient.relationshipToPatient !== CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS.Self">
          <ValidatedInput
            id="signatureRelationship"
            label="Relationship to Patient"
            v-model="form.state.$v.signatureRelationship.$model"
            :validation="form.state.$v.signatureRelationship"
            :inputComponent="InputText"
            :inputAttrs="{ placeholder: 'Relationship' }"
          />
        </MnColumn>
      </MnRow>
    </fieldset>
    <Button type="submit" label="Submit Complaint" click="submitComplaint"/>
  </form>

  <Dialog v-model:visible="isSubmissionCompleteOpen" modal header="Submission Complete" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.CheckCircle, 'color-success', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          Thank you for submitting your complaint. The reference number for your consumer complaint is #<b>{{ (savedForm.internalId !== undefined)?savedForm.internalId:savedForm.complaintId }}</b> Please keep this complaint number for your records. You will also receive an email confirmation about your complaint. Please refer to the instructions in the email for further steps.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog(); router.push({path: '/', force: true});" label="Done" severity="secondary"/>
      </div>
    </template>
  </Dialog>
  <Dialog v-model:visible="isSubmissionErrorOpen" modal header="Submission Error" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.ErrorCircle, 'color-danger', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          An error has occurred with submitting the form.
        </p>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-content-end">
        <Button type="button" class="me-2" rounded raised @click="closeDialog();" label="Done" severity="secondary"/>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import DatePicker from 'primevue/datepicker';
import TextArea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import RadioButton from 'primevue/radiobutton';
import Button from 'primevue/button';
import PageHeader from "@/components/common/PageHeader.vue";
import {ConsumerComplaintModel} from "@/models/consumer_complaint/ConsumerComplaintModel.js";
import {ConsumerComplaintProviderModel} from "@/models/consumer_complaint/ConsumerComplaintProviderModel.js";
import {submitComplaint} from "@/services/consumerComplaintService.js";
import {sendComplaintConfirmationEmail} from '@/services/emailService.js';
import MnRow from "@/components/common/MnRow.vue";
import MnColumn from "@/components/common/MnColumn.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import {useForm} from "@/composables/form.js";
import {computed, nextTick, ref} from "vue";
import ValidatedInput from "@/components/common/ValidatedInput.vue";
import {
  CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES,
  CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS,
  CONSUMER_COMPLAINT_TYPE_OF_COVERAGES,
  ICONS,
  CONSUMER_COMPLAINT_TYPES
} from "@/utils/constants.js";
import Select from "primevue/select";
import ReadOnlyText from "@/components/common/ReadOnlyText.vue";
import Dialog from "primevue/dialog";
import {useDialogStore} from "@/stores/dialogStore.js";
import router from "@/router/index.js";
import {sendAppealConfirmationEmail} from "@/services/emailService.js";

const dialogStore = useDialogStore();
const toggleDialog = (name) => dialogStore.toggle(name);
const closeDialog = () => dialogStore.close();
const savedForm = ref(null);
const model = new ConsumerComplaintModel();
const form = useForm(model, model.getValidationRules());

const isSubmissionCompleteOpen = computed(() => dialogStore.isOpen('submissionComplete'));
const isSubmissionErrorOpen = computed(() => dialogStore.isOpen('submissionError'));

const consumerComplaintSupportingDocuments = ref();
const isNothingToUpload = ref(false);

const complaintTypes = ref([]);
const manualComplaintExplanation = ref('');

const patientRelationshipOptions = computed(() => {
  return Object.entries(CONSUMER_COMPLAINT_PATIENT_RELATIONSHIPS).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const healthInsuranceCompanyOptions = computed(() => {
  return Object.entries(CONSUMER_COMPLAINT_HEALTH_INSURANCE_COMPANIES).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const typeOfCoverageOptions = computed(() => {
  return Object.entries(CONSUMER_COMPLAINT_TYPE_OF_COVERAGES).map(([k, v]) => ({
    key: k,
    value: v
  }));
});

const currentSummaryLength = computed(() => {
  return form.state.$v.summaryOfComplaint.$model
      ? form.state.$v.summaryOfComplaint.$model.length
      : 0;
});

const todayDate = computed(() => {
  const today = new Date();
  return today.toLocaleDateString();
});

const selectedComplaintTypes = computed(() => complaintTypes.value || []);

const onSubmit = async () => {
  form.state.$v.$touch();
  form.state.$v.patient.$touch();
  form.state.$v.healthPlan.$touch();
  form.state.$v.authorizedRep.$touch();
  form.state.$v.providers.$touch();
  console.log(form.state.$v);
  console.log(form.state.$v.$errors);
  if (form.state.$v.$invalid) {
    console.log('Consumer Complaint form state is invalid!' + form.state.data);
    await nextTick(() => form.focusInvalidInput());
    return;
  }
  try {
    const formData = form.state.data;

    // Attach selected complaint types
    formData.complaintTypes = complaintTypes.value.map(typeKey => ({
      complaintType: typeKey,
      manualComplaintExplanation: typeKey === 'SomethingElse' ? manualComplaintExplanation.value : null
    }));
    console.log('Submitting consumer complaint form data: ', formData);
    await addUploadedFilesToModel();
    // Submit data to API
    savedForm.value = await submitComplaint(formData);
    if (savedForm.value && savedForm.value.complaintId) {
      toggleDialog('submissionComplete');
      console.log('Setup dialog content to submissionComplete');
    }
    console.log('Complaint submitted successfully:', savedForm);
    // Send appeal confirmation email after the complaint submission.
    console.log('Sending complaint confirmation email to : ', formData.patient.email);
    console.log('Complaint ID: ', savedForm.value.complaintId);
    if (formData.patient?.email?.trim()) {
      await sendComplaintConfirmationEmail(formData.patient.email, (savedForm.value.internalId!==undefined)?savedForm.value.internalId:savedForm.value.complaintId);
    }
  } catch (error) {
    toggleDialog('submissionError');
    console.error('Error while submitting complaint:', error);
  }
};

const vFormData = computed({
  get() {
    return form.state.$v;
  },
  set(value) {
    // intentionally no-op because validation state is readonly
  }
});

const patient = computed({
  get() {
    return form.state.data.patient;
  },
  set(value) {
    form.state.data.patient = value;
  }
});

const vPatient = computed({
  get() {
    return form.state.$v.patient;
  },
  set(value) {
    // intentionally no-op because validation state is readonly
  }
});

const authorizedRep = computed({
  get() {
    return form.state.data.authorizedRep;
  },
  set(value) {
    form.state.data.authorizedRep = value;
  }
});

const vAuthorizedRep = computed({
  get() {
    return form.state.$v.authorizedRep;
  },
  set(value) {
    // intentionally no-op because validation state is readonly
  }
});

const healthPlan = computed({
  get() {
    return form.state.data.healthPlan;
  },
  set(value) {
    form.state.data.healthPlan = value;
  }
});

const vHealthPlan = computed({
  get() {
    return form.state.$v.healthPlan;
  },
  set(value) {
    // intentionally no-op because validation state is readonly
  }
});

const MAX_PROVIDERS = 10;

const providers = computed({
  get() {
    return form.state.data.providers;
  },
  set(value) {
    form.state.data.providers = value;
  }
});

const vProviders = computed(() => {
  return form.state.$v.providers?.$each ?? form.state.data.providers.map(() => ({}));
});

const addProvider = async () => {
  if (providers.value.length < MAX_PROVIDERS) {
    providers.value.push(new ConsumerComplaintProviderModel());
    await nextTick();
    await form.state.$v.value.$validate(); // Rebuild validation
  }
};

const removeProvider = async (index) => {
  if (providers.value.length > 1) {
    providers.value.splice(index, 1);
    await nextTick();
    await form.state.$v.value.$validate(); // Rebuild validation
  }
};

const addUploadedFilesToModel = async () => {
  const files = await consumerComplaintSupportingDocuments.value?.getUploadedFiles?.();
  if (!files || !Array.isArray(files)) return [];

  form.state.data.documents = files.map(file => ({
    type: file.type,
    url: file.name,
  }));

  return files;
};
</script>

<style scoped>
.manual-explanation-textarea {
  width: 100%;
  max-width: 800px;
}
</style>